import { APP_JSON } from '../../constants/common-enums';
import Browser from '../../helpers/browser';
import fetchReq from '../../helpers/fetch';
import { mark, marks } from '../../helpers/instrumentation';
import StaticIdSingletonClass from '../../helpers/static-id';
import CONFIG from '../../settings';

const API = {
  /**
   * Spawn Token api for ERM
   *  - 201 ok, set `activity` http server side cookie in response headers.
   *  - 201 ok, returns response payload with new activity.
   *  - takes access_token received as part of ERM activity API response
   *  - TODO Unit test script
   *  - Removed 'urn:ext:ietf:params:oauth:response-type:id_token' from the response_type
   *    array due to VBOX13 merchant apiKey config issue.
   * @param {string} token
   * @returns {object} response object
   */
  spawnForErm(token) {
    mark(marks.spawnErmCookieStart);
    const ermRequestPayload = {
      grant_type: 'token_spawn',
      response_mode: ['urn:ext:oauth:response-mode:body', 'urn:ext:oauth:response-mode:cookie'],
      response_type: [
        'urn:ext:ietf:params:oauth:response-type:activity_ref',
        'urn:ext:ietf:params:oauth:response-type:id_token'
      ],
      subject_token: token,
      subject_token_type_hint: 'access_token'
    };

    // F15857 - if browser supports samesite=none, pass options to for ARM to set it when it writes cookies
    if (Browser.supportsSameSiteNone()) {
      ermRequestPayload.options = {
        response_type: [{ usage_context: 'THIRD_PARTY' }, null]
      };
    }

    return this.spawnToken(ermRequestPayload).then(res => {
      mark(marks.spawnErmCookieEnd);
      return res;
    });
  },

  /**
   * Spawn Token api for ID Token
   *  - 201 ok, returns response payload with new id token.
   *  - takes access_token received as part of Verify API response
   *  - TODO Unit test script
   * @param {string} token
   * @returns {object} response object
   */
  spawnForIdToken(token) {
    const idTokenRequestPayload = {
      grant_type: 'token_spawn',
      response_mode: ['urn:ext:oauth:response-mode:body'],
      response_type: ['urn:ext:ietf:params:oauth:response-type:id_token'],
      subject_token: token,
      subject_token_type_hint: 'access_token'
    };
    return this.spawnToken(idTokenRequestPayload);
  },

  /**
   * Spawn Token api to set the server/client side remember_me cookies. Refernece F11669 feature
   *  - 201 ok, set `remember_me`(client),`remember_me_code`(http) & `remember_me_code_vdcp`(http) client/http server side cookies in response headers. No response payload/body
   *  - takes access_token received as part of sidToken API response
   *  - TODO Unit test script
   * @param {string} token
   * @returns {object} response object
   */
  spawnForRememberMe(token) {
    const remembermeRequestPayload = {
      grant_type: 'token_spawn',
      response_mode: [
        'urn:ext:oauth:response-mode:cookie',
        'urn:ext:oauth:response-mode:cookie',
        'urn:ext:oauth:response-mode:cookie'
      ],
      response_type: [
        'urn:ext:ietf:params:oauth:response-type:remember_me',
        'urn:ext:ietf:params:oauth:response-type:remember_me_code',
        'urn:ext:ietf:params:oauth:response-type:remember_me_code_vdcp'
      ],
      subject_token: token,
      subject_token_type_hint: 'access_token'
    };

    // F15857 - if browser supports samesite=none, pass options to for ARM to set it when it writes cookies
    if (Browser.supportsSameSiteNone()) {
      remembermeRequestPayload.options = {
        response_type: [
          { usage_context: 'THIRD_PARTY' },
          { usage_context: 'THIRD_PARTY' },
          { usage_context: 'THIRD_PARTY' }
        ]
      };
    }

    return this.spawnToken(remembermeRequestPayload);
  },

  /**
   * Spawn Token api for SSI
   *  - 201 ok, set `secured_id_token` http server side cookie in response headers. No response payload/body
   *  - takes access_token received as part of sidToken API response
   *  - TODO Unit test script
   * @param {string} token
   * @returns {object} response object
   */
  spawnForSsi(token) {
    const ssiRequestPayload = {
      grant_type: 'token_spawn',
      response_mode: ['urn:ext:oauth:response-mode:body', 'urn:ext:oauth:response-mode:cookie'],
      response_type: [
        'urn:ext:ietf:params:oauth:response-type:secured_id_token',
        'urn:ext:ietf:params:oauth:response-type:id_token'
      ],
      subject_token: token
    };

    // F15857 - if browser supports samesite=none, pass options to for ARM to set it when it writes cookies
    if (Browser.supportsSameSiteNone()) {
      ssiRequestPayload.options = {
        response_type: [{ usage_context: 'THIRD_PARTY' }, null]
      };
    }

    return this.spawnToken(ssiRequestPayload);
  },

  /**
   * Generic Spawn Token api
   *  - no response payload when http status code is 201 ok.
   *  - takes requestPayload constructed as part of sidToken/ERM Activity API Responses
   *  - TODO Unit test script
   * @param {object} requestPayload
   * @returns {object} response object
   */
  spawnToken(requestPayload) {
    const base64ApiKey = btoa(StaticIdSingletonClass.apikey);
    const options = {
      body: requestPayload,
      headers: {
        Accept: APP_JSON,
        Authorization: `Basic ${base64ApiKey}`,
        'Content-Type': APP_JSON
      },
      method: 'POST',
      url: CONFIG.application.api.token.spawn(StaticIdSingletonClass.namespace)
    };

    return fetchReq(options);
  }
};

export default API;
